import React, { useMemo, useState } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';

/*
  CategoryDonut: a compact single-ring donut of category amounts with a legend
  beneath. By default every category is shown ungrouped; passing `maxSlices`
  collapses the long tail into an "Other" slice so the chart stays legible.
  The Overview "Totals" card toggles which side (income / expenses) is shown.

  Selection: clicking a slice highlights its legend row, and clicking a legend
  row highlights its slice — the other slices dim and the centre figure swaps
  to the selected category. Clicking the same one again (or anything else)
  moves/clears the selection.

  Props:
   - items: [{ id, name, amount, color }]
   - total: number (denominator for the legend percentages + centre figure)
   - formatCurrency: (amount, decimals) => string
   - accentColor: palette path for the centre figure (e.g. 'error.main')
   - centerLabel: label above the centre figure (e.g. 'In' / 'Out')
   - maxSlices: number of individual slices before the rest collapse into
       "Other". When null/undefined every category is shown (ungrouped).
*/

const PALETTE = [
  '#F41468', '#6366F1', '#10B981', '#F59E0B', '#3B82F6', '#EC4899',
  '#14B8A6', '#8B5CF6', '#EF4444', '#84CC16', '#06B6D4', '#A855F7',
];

export default function CategoryDonut({ items, total, formatCurrency, accentColor, centerLabel = 'Total', maxSlices = null }) {
  // Id of the slice/legend row the user clicked. Kept as an id (not an index)
  // so it survives regrouping; if the id disappears (e.g. the In/Out toggle
  // swaps the dataset) it simply no longer matches and nothing is selected.
  const [selectedId, setSelectedId] = useState(null);

  const slices = useMemo(() => {
    const sorted = items.filter(i => i.amount > 0).sort((a, b) => b.amount - a.amount);
    const colored = sorted.map((it, i) => ({
      id: it.id,
      name: it.name,
      amount: it.amount,
      color: it.color || PALETTE[i % PALETTE.length],
    }));

    // Ungrouped unless a slice cap is set and we actually exceed it.
    if (maxSlices == null || colored.length <= maxSlices) return colored;

    const top = colored.slice(0, maxSlices);
    const rest = colored.slice(maxSlices);
    top.push({
      id: '__other__',
      name: `Other (${rest.length})`,
      amount: rest.reduce((sum, r) => sum + r.amount, 0),
      color: '#CBD5E1',
    });
    return top;
  }, [items, maxSlices]);

  const selected = slices.find(s => s.id === selectedId) || null;
  const toggleSelected = (id) => setSelectedId(prev => (prev === id ? null : id));

  if (!slices.length) return null;

  const selectedPct = selected && total > 0 ? (selected.amount / total) * 100 : 0;

  return (
    <Box>
      <Box sx={{ position: 'relative', height: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={slices}
              dataKey="amount"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={62}
              outerRadius={90}
              paddingAngle={1}
              stroke="none"
              onClick={(_, index) => slices[index] && toggleSelected(slices[index].id)}
            >
              {slices.map((s) => (
                <Cell
                  key={s.id}
                  fill={s.color}
                  // Dim everything except the selected slice so the pair
                  // (slice + legend row) reads as one highlighted unit.
                  fillOpacity={selected && s.id !== selected.id ? 0.25 : 1}
                  style={{ cursor: 'pointer', transition: 'fill-opacity 150ms ease', outline: 'none' }}
                />
              ))}
            </Pie>
            <RechartsTooltip
              // High z-index keeps the tooltip above the centre In/Out overlay.
              wrapperStyle={{ zIndex: 30, outline: 'none' }}
              content={({ active, payload }) => {
                if (!active || !payload || !payload.length) return null;
                const slice = payload[0];
                return (
                  <Box sx={{
                    bgcolor: '#fff',
                    border: '1px solid',
                    borderColor: 'grey.200',
                    borderRadius: 2,
                    boxShadow: '0 8px 20px rgba(15,23,42,0.18)',
                    px: 1.5,
                    py: 1,
                    minWidth: 128,
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                      <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: slice.payload.color, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: 'text.secondary' }}>
                        {slice.name}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.1 }}>
                      {formatCurrency(slice.value, 0)}
                    </Typography>
                  </Box>
                );
              }}
            />
          </PieChart>
        </ResponsiveContainer>
        {/* Centre figure — the In/Out total, or the selected category when a
            slice/legend row is picked. zIndex 1 keeps it above the ring but
            below the tooltip (zIndex 30). */}
        <Box sx={{
          position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', zIndex: 1,
          textAlign: 'center', px: 6,
        }}>
          {selected ? (
            <>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 104 }}>
                {selected.name}
              </Typography>
              <Typography fontWeight="bold" sx={{ fontSize: '1.15rem', letterSpacing: '-0.02em', color: selected.color }}>
                {formatCurrency(selected.amount, 0)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedPct.toFixed(0)}%
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="caption" color="text.secondary">{centerLabel}</Typography>
              <Typography fontWeight="bold" sx={{ fontSize: '1.15rem', letterSpacing: '-0.02em', color: accentColor || 'text.primary' }}>
                {formatCurrency(total, 0)}
              </Typography>
            </>
          )}
        </Box>
      </Box>

      {/* Legend — each row toggles the same selection as its donut slice */}
      <Box sx={{ mt: 1.5 }}>
        {slices.map((s) => {
          const pct = total > 0 ? (s.amount / total) * 100 : 0;
          const isSelected = selected?.id === s.id;
          return (
            <Box
              key={s.id}
              role="button"
              tabIndex={0}
              aria-pressed={isSelected}
              onClick={() => toggleSelected(s.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleSelected(s.id);
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 0.6,
                px: 1,
                mx: -1,
                borderRadius: 1.5,
                cursor: 'pointer',
                transition: 'background-color 120ms ease, opacity 150ms ease',
                bgcolor: isSelected ? alpha(s.color, 0.12) : 'transparent',
                opacity: selected && !isSelected ? 0.45 : 1,
                '&:hover': { bgcolor: isSelected ? alpha(s.color, 0.18) : 'grey.100' },
              }}
            >
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.color, flexShrink: 0 }} />
              <Tooltip title={s.name} placement="top">
                <Typography variant="body3" sx={{ flex: 1, minWidth: 0, fontWeight: isSelected ? 700 : 500 }} noWrap>
                  {s.name}
                </Typography>
              </Tooltip>
              <Typography variant="body3" fontWeight="bold" sx={{ flexShrink: 0 }}>{formatCurrency(s.amount, 0)}</Typography>
              <Typography variant="body3" color="text.secondary" sx={{ width: 44, textAlign: 'right', flexShrink: 0 }}>
                {pct.toFixed(0)}%
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
